import React, { useState, useRef } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { X, Image as ImageIcon } from 'lucide-react'
import { ComicPanel as ComicPanelType, DialogBubbleData, ImageAsset } from '../types'
import { DialogBubble } from './DialogBubble'

interface ComicPanelProps {
  panel: ComicPanelType
  dialogBubbles: DialogBubbleData[]
  selectedBubble: string | null
  selectedPanel: string | null
  onSelectBubble: (id: string | null) => void
  onSelectPanel: (id: string) => void
  onUpdateBubble: (id: string, updates: Partial<DialogBubbleData>) => void
  onDeleteBubble: (id: string) => void
  onUpdatePanel: (id: string, updates: Partial<ComicPanelType>) => void
  onShowPanel: () => void
  onSetPanelBg: (panelId: string, imageUrl: string) => void
  imageAssets: ImageAsset[]
}

export const ComicPanel: React.FC<ComicPanelProps> = ({
  panel,
  dialogBubbles,
  selectedBubble,
  selectedPanel,
  onSelectBubble,
  onSelectPanel,
  onUpdateBubble,
  onDeleteBubble,
  onUpdatePanel,
  onShowPanel,
  onSetPanelBg,
  imageAssets: _imageAssets
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const { setNodeRef, isOver } = useDroppable({
    id: `panel-${panel.id}`,
    data: { type: 'panel', panelId: panel.id }
  })

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    // 支持侧边栏图片拖拽
    const assetUrl = e.dataTransfer.getData('image-asset-url')
    if (assetUrl) {
      onSetPanelBg(panel.id, assetUrl)
      return
    }
    const files = Array.from(e.dataTransfer.files)
    const imageFile = files.find(file => file.type.startsWith('image/'))
    
    if (imageFile) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string
        onUpdatePanel(panel.id, { backgroundImage: imageUrl })
      }
      reader.readAsDataURL(imageFile)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string
        onUpdatePanel(panel.id, { backgroundImage: imageUrl })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    onUpdatePanel(panel.id, { backgroundImage: undefined })
  }

  const handlePanelClick = (e: React.MouseEvent) => {
    // 点击面板选择为工作区域
    onSelectPanel(panel.id)
    // 如果点击的是面板空白区域，清除气泡选择
    if (e.target === e.currentTarget) {
      onSelectBubble(null)
    }
  }

  // 判断是否为当前选中面板
  const isSelectedPanel = selectedPanel === panel.id

  const panelBubbles = dialogBubbles.filter(bubble => bubble.panelId === panel.id)

  return (
    <div
      ref={setNodeRef}
      className={`absolute transition-all duration-200 overflow-hidden ${
        isSelectedPanel ? 'border-2 border-blue-500' : 
        isOver ? 'border-2 border-primary-400 bg-primary-50' : 'border-2 border-gray-300'
      } ${isDragging ? 'border-2 border-primary-400 bg-primary-50' : ''}`}
      style={{
        left: panel.x,
        top: panel.y,
        width: panel.width,
        height: panel.height,
        backgroundColor: panel.backgroundColor,
        borderColor: panel.borderColor,
        borderWidth: panel.borderWidth,
        borderRadius: panel.borderRadius,
        // 移除backgroundImage，改为img标签渲染
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handlePanelClick}
    >
      {/* 选中面板指示器 - 简化版，移除闪烁动画 */}
      {isSelectedPanel && (
        <div className="absolute top-2 left-2 z-30 px-2 py-1 text-xs font-medium text-white bg-blue-500 rounded shadow-sm select-none export-hide">
          当前工作区域
        </div>
      )}

      {/* 面板序号 - 已移除编号显示 */}
      {/* <div className="absolute top-2 left-2 z-10 px-2 py-1 text-xs font-bold text-white bg-gray-500 bg-opacity-70 rounded-lg shadow-sm select-none export-hide">
        面板{panel.order}
      </div> */}

      {/* 背景图片控制 */}
      {!panel.backgroundImage ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-80 rounded">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
              <ImageIcon size={32} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-600 mb-2">拖拽图片到这里</p>
            <p className="text-xs text-gray-500 mb-3">或点击选择文件</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="glass-button text-xs px-3 py-1"
            >
              选择图片
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      ) : (
        <>
          <img
            src={panel.backgroundImage}
            alt="panel-bg"
            className="absolute inset-0 w-full h-full object-cover rounded-[14px] shadow-lg pointer-events-none select-none"
            style={{ zIndex: 1 }}
          />
          <button
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center z-20 opacity-80 hover:opacity-100 transition-opacity export-hide"
            title="移除背景图片"
          >
            <X size={12} />
          </button>
        </>
      )}

      {/* 渲染对话气泡 */}
      {panelBubbles.map((bubble) => (
        <DialogBubble
          key={bubble.id}
          bubble={bubble}
          isSelected={selectedBubble === bubble.id}
          onClick={() => {
            onSelectBubble(bubble.id)
            onShowPanel()
          }}
          onUpdate={onUpdateBubble}
          onDelete={() => onDeleteBubble(bubble.id)}
        />
      ))}
    </div>
  )
} 